import type { RenderContext } from "../types.js";
import { attr, jsString, text, url } from "../escape.js";
import { sanitizeThemeTokenColor } from "../css-cascade.js";

export interface ServiceBookingBlockProps {
  heading?: string;
  serviceId?: string;
  calendarId?: string;
  duration?: number;
  showTimezone?: boolean;
  bookingUrl?: string;
  primaryColor?: string;
  availabilityApiUrl?: string;
}

function buildScript(uid: string, initialServiceId: string, availabilityApiUrl: string): string {
  return `<script>(function(){
    var root=document.querySelector('[data-booking-id="${jsString(uid)}"]');
    if(!root)return;
    var tenantId=root.getAttribute('data-tenant-id')||'';
    var bookingUrl=root.getAttribute('data-booking-url')||'';
    var servicesApi='/api/public/bookings/services';
    var intentApi='/api/public/bookings/intent';
    var bookingsApi='/api/public/bookings';
    var availabilityApi='${jsString(availabilityApiUrl)}';
    var initialServiceId='${jsString(initialServiceId)}';
    var modeWrap=root.querySelector('[data-booking-modes]');
    var discoveryWrap=root.querySelector('[data-booking-discovery]');
    var servicesWrap=root.querySelector('[data-booking-services]');
    var datesWrap=root.querySelector('[data-booking-dates]');
    var slotsWrap=root.querySelector('[data-booking-slots]');
    var timezoneWrap=root.querySelector('[data-booking-timezone]');
    var formWrap=root.querySelector('[data-booking-form]');
    var statusWrap=root.querySelector('[data-booking-status]');
    var titleWrap=root.querySelector('[data-booking-selection-title]');
    if(!tenantId||!modeWrap||!discoveryWrap||!servicesWrap||!datesWrap||!slotsWrap||!formWrap||!statusWrap||!titleWrap){return;}

    var state={
      services:[],
      mode:'simple_list',
      query:'',
      category:null,
      selectedServiceId:initialServiceId||'',
      selectedDate:'',
      selectedSlot:null,
      intent:{summary:'',source:'ai',matches:[],loading:false,error:''}
    };

    function h(tag,attrs,children){
      var el=document.createElement(tag);
      if(attrs){
        Object.keys(attrs).forEach(function(key){
          var value=attrs[key];
          if(value===undefined||value===null)return;
          if(key==='className'){el.className=String(value);return;}
          if(key==='style'){el.setAttribute('style',String(value));return;}
          if(key==='text'){el.textContent=String(value);return;}
          if(key.indexOf('on')===0&&typeof value==='function'){el.addEventListener(key.slice(2).toLowerCase(),value);return;}
          el.setAttribute(key,String(value));
        });
      }
      if(children){
        children.forEach(function(child){
          if(child===null||child===undefined)return;
          if(typeof child==='string'){el.appendChild(document.createTextNode(child));return;}
          el.appendChild(child);
        });
      }
      return el;
    }

    function serviceById(id){return state.services.find(function(service){return service.id===id;})||null;}
    function intentMatchById(id){return state.intent.matches.find(function(match){return match.serviceId===id;})||null;}
    function formatMoney(amount,currency){if(amount===null||amount===undefined)return'Pricing available on request';try{return new Intl.NumberFormat(undefined,{style:'currency',currency:currency||'EUR',minimumFractionDigits:0,maximumFractionDigits:0}).format(amount/100);}catch{return currency+' '+String(amount/100);}}
    function getUpcomingDates(){var items=[];var today=new Date();today.setHours(0,0,0,0);for(var i=0;i<10;i+=1){var next=new Date(today);next.setDate(today.getDate()+i);items.push(next);}return items;}
    function toDateParam(date){return date.getFullYear()+'-'+String(date.getMonth()+1).padStart(2,'0')+'-'+String(date.getDate()).padStart(2,'0');}
    function formatDateLabel(date){return{short:date.toLocaleDateString(undefined,{weekday:'short',day:'numeric'}),long:date.toLocaleDateString(undefined,{weekday:'long',month:'long',day:'numeric'})};}
    function formatSlotLabel(value){return new Date(value).toLocaleTimeString([], {hour:'numeric',minute:'2-digit'});}
    function visibleServices(){
      if(state.mode==='intent_first'&&state.query.trim()){
        var matches=state.intent.matches.map(function(match){return serviceById(match.serviceId);}).filter(function(service){return Boolean(service);});
        return matches;
      }
      if(state.mode==='category_browse'&&state.category){
        return state.services.filter(function(service){return service.categoryLabel===state.category;});
      }
      return state.services;
    }

    function renderModes(){
      modeWrap.innerHTML='';
      var modes=[
        {id:'intent_first',label:'Intent-first',copy:'Describe the outcome you want and let AI rank the closest live services.'},
        {id:'category_browse',label:'Browse by category',copy:'Move from service family to the exact service you want.'},
        {id:'simple_list',label:'Simple list',copy:'Choose from the full service list and book directly.'}
      ];
      modes.forEach(function(mode){
        var active=state.mode===mode.id;
        modeWrap.appendChild(h('button',{type:'button',style:'border:1px solid '+(active?'var(--ease-color-brand,#2563eb)':'var(--ease-color-border,var(--ease-color-muted,#e5e7eb))')+';background:'+(active?'var(--ease-color-brand,#2563eb)':'var(--ease-color-bg,#fff)')+';color:'+(active?'#fff':'var(--ease-color-text,#1f2937)')+';border-radius:18px;padding:14px 16px;text-align:left;cursor:pointer'},[
          h('div',{style:'font-weight:600'},[mode.label]),
          h('div',{style:'margin-top:4px;font-size:12px;line-height:1.5;opacity:'+(active?'0.84':'1')},[mode.copy])
        ]));
      });
      Array.prototype.forEach.call(modeWrap.querySelectorAll('button'),function(button,index){
        button.addEventListener('click',function(){
          state.mode=modes[index].id;
          if(state.mode!=='intent_first'){state.intent={summary:'',source:'ai',matches:[],loading:false,error:''};}
          renderDiscovery();
          renderServices();
        });
      });
    }

    function renderDiscovery(){
      discoveryWrap.innerHTML='';
      if(state.mode==='intent_first'){
        var input=h('input',{type:'text',value:state.query,placeholder:'Example: I need a deep home clean before the weekend',style:'width:100%;height:48px;border:1px solid var(--ease-color-border,var(--ease-color-muted,#e5e7eb));border-radius:18px;padding:0 16px;background:var(--ease-color-surface,var(--ease-color-bg,#fff));color:var(--ease-color-text,#1f2937)'});
        input.addEventListener('input',function(event){
          state.query=event.target.value||'';
          runIntentSearch();
        });
        discoveryWrap.appendChild(h('label',{style:'display:block;font-size:13px;font-weight:600;color:var(--ease-color-text,#1f2937)'},['What do you need help with?']));
        discoveryWrap.appendChild(h('div',{style:'margin-top:10px'},[input]));
        discoveryWrap.appendChild(h('p',{style:'margin:10px 0 0;font-size:12px;line-height:1.6;color:var(--ease-color-text-soft,#6b7280)'},['Stero interprets the request, ranks the best public services, and then loads real slots for the selected match.']));
        if(state.query.trim().length>=2){
          discoveryWrap.appendChild(h('div',{style:'margin-top:14px;border:1px solid var(--ease-color-border,var(--ease-color-muted,#e5e7eb));border-radius:18px;padding:14px;background:var(--ease-color-surface,var(--ease-color-bg,#fff))'},[
            h('div',{style:'display:flex;justify-content:space-between;gap:12px;align-items:center'},[
              h('div',{style:'font-size:13px;font-weight:600;color:var(--ease-color-text,#1f2937)'},['AI service matching']),
              h('div',{style:'border-radius:999px;padding:4px 10px;background:color-mix(in srgb, var(--ease-color-brand,#2563eb) 10%, white);font-size:11px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:var(--ease-color-brand,#2563eb)'},[state.intent.source==='fallback'?'Fallback':'AI'])
            ]),
            h('p',{style:'margin:10px 0 0;font-size:13px;line-height:1.6;color:var(--ease-color-text-soft,#6b7280)'},[
              state.intent.loading?'Interpreting the request against the live public catalog...':(state.intent.error||state.intent.summary||'Type a bit more context and Stero will rank the strongest matches.')
            ])
          ]));
        }
        return;
      }
      if(state.mode==='category_browse'){
        var categories=[];
        state.services.forEach(function(service){if(categories.indexOf(service.categoryLabel)===-1){categories.push(service.categoryLabel);}});
        if(!state.category&&categories.length){state.category=categories[0];}
        var pills=h('div',{style:'display:flex;flex-wrap:wrap;gap:8px'},[]);
        categories.forEach(function(category){
          var active=state.category===category;
          pills.appendChild(h('button',{type:'button',style:'border:1px solid '+(active?'var(--ease-color-brand,#2563eb)':'var(--ease-color-border,var(--ease-color-muted,#e5e7eb))')+';background:'+(active?'var(--ease-color-brand,#2563eb)':'var(--ease-color-bg,#fff)')+';color:'+(active?'#fff':'var(--ease-color-text,#1f2937)')+';border-radius:999px;padding:8px 14px;cursor:pointer'},[category]));
        });
        pills.addEventListener('click',function(event){
          var target=event.target;
          if(!(target instanceof HTMLButtonElement))return;
          state.category=target.textContent||null;
          renderDiscovery();
          renderServices();
        });
        discoveryWrap.appendChild(pills);
      }
    }

    function renderServices(){
      servicesWrap.innerHTML='';
      var items=visibleServices();
      if(state.mode==='intent_first'&&state.query.trim()&&!state.intent.loading&&items.length===0){
        servicesWrap.appendChild(h('div',{style:'border:1px dashed var(--ease-color-border,var(--ease-color-muted,#e5e7eb));border-radius:20px;padding:18px;background:var(--ease-color-bg,#fff);color:var(--ease-color-text-soft,#6b7280)'},['No strong public service match yet. Refine the request with the outcome, service type, or location preference.']));
        return;
      }
      if(items.length===0){
        servicesWrap.appendChild(h('div',{style:'border:1px dashed var(--ease-color-border,var(--ease-color-muted,#e5e7eb));border-radius:20px;padding:18px;background:var(--ease-color-bg,#fff);color:var(--ease-color-text-soft,#6b7280)'},['No public services match this selection yet.']));
        return;
      }
      items.forEach(function(service){
        var active=state.selectedServiceId===service.id;
        var match=intentMatchById(service.id);
        servicesWrap.appendChild(h('button',{type:'button','data-service-id':service.id,style:'display:block;width:100%;border:1px solid '+(active?'var(--ease-color-brand,#2563eb)':'var(--ease-color-border,var(--ease-color-muted,#e5e7eb))')+';background:'+(active?'color-mix(in srgb, var(--ease-color-brand,#2563eb) 8%, white)':'var(--ease-color-bg,#fff)')+';color:var(--ease-color-text,#1f2937);border-radius:24px;padding:18px;text-align:left;cursor:pointer'},[
          h('div',{style:'display:flex;justify-content:space-between;gap:16px;align-items:start'},[
            h('div',{},[
              h('div',{style:'font-weight:600'},[service.name]),
              h('div',{style:'margin-top:6px;font-size:13px;line-height:1.65;color:var(--ease-color-text-soft,#6b7280)'},[service.description||'Live availability loads as soon as you choose a date.']),
              match?h('div',{style:'margin-top:10px;font-size:12px;line-height:1.6;color:var(--ease-color-brand,#2563eb)'},[match.reason]):null
            ]),
            h('div',{style:'text-align:right;font-size:12px;color:var(--ease-color-text-soft,#6b7280)'},[
              h('div',{},[service.durationMinutes+' min']),
              h('div',{style:'margin-top:6px'},[formatMoney(service.priceCents,service.currency)]),
              match?h('div',{style:'margin-top:8px;font-size:11px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:var(--ease-color-brand,#2563eb)'},[String(Math.round((match.confidence||0)*100))+'% match']):null
            ])
          ]),
          h('div',{style:'margin-top:12px;font-size:11px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:var(--ease-color-brand,#2563eb)'},[service.categoryLabel])
        ]));
      });
      Array.prototype.forEach.call(servicesWrap.querySelectorAll('button[data-service-id]'),function(button){
        button.addEventListener('click',function(){
          state.selectedServiceId=button.getAttribute('data-service-id')||'';
          state.selectedSlot=null;
          renderServices();
          renderSummary();
          renderDates();
        });
      });
      renderSummary();
      renderDates();
    }

    function renderSummary(){
      var service=serviceById(state.selectedServiceId);
      titleWrap.innerHTML='';
      if(!service){titleWrap.textContent='Select a service';return;}
      titleWrap.textContent=service.name+' · '+service.durationMinutes+' min';
    }

    function renderDates(){
      datesWrap.innerHTML='';
      var service=serviceById(state.selectedServiceId);
      if(!service){
        datesWrap.appendChild(h('p',{style:'margin:0;color:var(--ease-color-text-soft,#6b7280)'},['Choose a service to load live availability.']));
        slotsWrap.innerHTML='';
        formWrap.innerHTML='';
        return;
      }
      getUpcomingDates().forEach(function(date){
        var label=formatDateLabel(date);
        var value=toDateParam(date);
        var active=state.selectedDate===value;
        datesWrap.appendChild(h('button',{type:'button','data-date':value,style:'border:1px solid '+(active?'var(--ease-color-brand,#2563eb)':'var(--ease-color-border,var(--ease-color-muted,#e5e7eb))')+';background:'+(active?'var(--ease-color-brand,#2563eb)':'var(--ease-color-surface,var(--ease-color-bg,#fff))')+';color:'+(active?'#fff':'var(--ease-color-text,#1f2937)')+';border-radius:18px;padding:12px 14px;text-align:left;cursor:pointer'},[
          h('div',{style:'font-size:11px;letter-spacing:.12em;text-transform:uppercase;opacity:.8'},[label.short]),
          h('div',{style:'margin-top:4px;font-size:13px;font-weight:600'},[label.long.split(', ')[0]])
        ]));
      });
      Array.prototype.forEach.call(datesWrap.querySelectorAll('button[data-date]'),function(button){
        button.addEventListener('click',function(){
          state.selectedDate=button.getAttribute('data-date')||'';
          state.selectedSlot=null;
          renderDates();
          fetchAvailability();
        });
      });
      if(!state.selectedDate){
        state.selectedDate=toDateParam(getUpcomingDates()[0]);
        renderDates();
        return;
      }
      fetchAvailability();
    }

    function setStatus(message,isError){
      statusWrap.innerHTML='';
      statusWrap.appendChild(h('div',{style:'border:1px solid '+(isError?'var(--ease-color-warning,var(--ease-color-accent,#f59e0b))':'var(--ease-color-border,var(--ease-color-muted,#e5e7eb))')+';border-radius:18px;padding:14px;background:'+(isError?'color-mix(in srgb, var(--ease-color-warning,var(--ease-color-accent,#f59e0b)) 8%, white)':'var(--ease-color-surface,var(--ease-color-bg,#fff))')+';color:var(--ease-color-text-soft,#6b7280);font-size:13px;line-height:1.6'},[message]));
    }

    function fetchAvailability(){
      var service=serviceById(state.selectedServiceId);
      if(!service||!state.selectedDate)return;
      slotsWrap.innerHTML='';
      formWrap.innerHTML='';
      setStatus('Checking live availability for '+service.name+' on '+state.selectedDate+'...',false);
      var params=new URLSearchParams({tenant_id:tenantId,service_id:service.id,date:state.selectedDate});
      fetch(availabilityApi+'?'+params.toString()).then(function(response){
        if(!response.ok)throw new Error('availability_request_failed');
        return response.json();
      }).then(function(payload){
        if(payload&&payload.data&&payload.data.timezone&&timezoneWrap){timezoneWrap.textContent='Timezone: '+payload.data.timezone;}
        if(payload&&payload.data&&payload.data.runtime&&payload.data.runtime.canSelfServeBook===false){
          slotsWrap.innerHTML='';
          formWrap.innerHTML='';
          setStatus(payload.data.runtime.message,true);
          return;
        }
        renderSlots(payload&&payload.data&&Array.isArray(payload.data.slots)?payload.data.slots:[]);
      }).catch(function(){
        slotsWrap.innerHTML='';
        formWrap.innerHTML='';
        setStatus('Live availability is temporarily unavailable. Use the fallback link if you need to capture demand immediately.',true);
      });
    }

    function renderSlots(slots){
      slotsWrap.innerHTML='';
      if(!slots.length){
        setStatus('No live slots are open on this date yet. Try another day or service.',true);
        return;
      }
      statusWrap.innerHTML='';
      slots.forEach(function(slot){
        var active=state.selectedSlot&&state.selectedSlot.start===slot.start;
        slotsWrap.appendChild(h('button',{type:'button','data-slot-start':slot.start,'data-slot-end':slot.end,style:'border:1px solid '+(active?'var(--ease-color-brand,#2563eb)':'var(--ease-color-border,var(--ease-color-muted,#e5e7eb))')+';background:'+(active?'var(--ease-color-brand,#2563eb)':'var(--ease-color-bg,#fff)')+';color:'+(active?'#fff':'var(--ease-color-text,#1f2937)')+';border-radius:18px;padding:12px 14px;text-align:left;cursor:pointer'},[
          h('div',{style:'font-weight:600'},[formatSlotLabel(slot.start)]),
          h('div',{style:'margin-top:4px;font-size:12px;opacity:.78'},['Live slot'])
        ]));
      });
      Array.prototype.forEach.call(slotsWrap.querySelectorAll('button[data-slot-start]'),function(button){
        button.addEventListener('click',function(){
          state.selectedSlot={start:button.getAttribute('data-slot-start')||'',end:button.getAttribute('data-slot-end')||''};
          renderSlots(slots);
          renderForm();
        });
      });
      renderForm();
    }

    function renderForm(){
      formWrap.innerHTML='';
      if(!state.selectedSlot)return;
      var service=serviceById(state.selectedServiceId);
      if(!service)return;
      var form=h('form',{style:'display:grid;gap:12px'},[
        h('div',{style:'font-size:13px;line-height:1.65;color:var(--ease-color-text-soft,#6b7280)'},['Booking '+service.name+' for '+new Date(state.selectedSlot.start).toLocaleString()+'.']),
        h('div',{style:'display:grid;gap:12px;grid-template-columns:repeat(2,minmax(0,1fr))'},[
          h('label',{style:'display:block;font-size:13px;color:var(--ease-color-text,#1f2937)'},[h('span',{style:'display:block;margin-bottom:6px;font-weight:600'},['First name']),h('input',{required:'true',name:'firstName',style:'width:100%;height:44px;border:1px solid var(--ease-color-border,var(--ease-color-muted,#e5e7eb));border-radius:14px;padding:0 14px;background:var(--ease-color-bg,#fff);color:var(--ease-color-text,#1f2937)'})]),
          h('label',{style:'display:block;font-size:13px;color:var(--ease-color-text,#1f2937)'},[h('span',{style:'display:block;margin-bottom:6px;font-weight:600'},['Last name']),h('input',{required:'true',name:'lastName',style:'width:100%;height:44px;border:1px solid var(--ease-color-border,var(--ease-color-muted,#e5e7eb));border-radius:14px;padding:0 14px;background:var(--ease-color-bg,#fff);color:var(--ease-color-text,#1f2937)'})]),
          h('label',{style:'display:block;font-size:13px;color:var(--ease-color-text,#1f2937)'},[h('span',{style:'display:block;margin-bottom:6px;font-weight:600'},['Email']),h('input',{required:'true',type:'email',name:'email',style:'width:100%;height:44px;border:1px solid var(--ease-color-border,var(--ease-color-muted,#e5e7eb));border-radius:14px;padding:0 14px;background:var(--ease-color-bg,#fff);color:var(--ease-color-text,#1f2937)'})]),
          h('label',{style:'display:block;font-size:13px;color:var(--ease-color-text,#1f2937)'},[h('span',{style:'display:block;margin-bottom:6px;font-weight:600'},['Phone']),h('input',{type:'tel',name:'phone',style:'width:100%;height:44px;border:1px solid var(--ease-color-border,var(--ease-color-muted,#e5e7eb));border-radius:14px;padding:0 14px;background:var(--ease-color-bg,#fff);color:var(--ease-color-text,#1f2937)'})])
        ]),
        h('label',{style:'display:block;font-size:13px;color:var(--ease-color-text,#1f2937)'},[h('span',{style:'display:block;margin-bottom:6px;font-weight:600'},['Notes']),h('textarea',{name:'notes',rows:'4',style:'width:100%;border:1px solid var(--ease-color-border,var(--ease-color-muted,#e5e7eb));border-radius:18px;padding:12px 14px;background:var(--ease-color-bg,#fff);color:var(--ease-color-text,#1f2937)'})]),
        h('button',{type:'submit',style:'height:46px;border:none;border-radius:18px;background:var(--ease-color-brand,#2563eb);color:#fff;font-weight:700;cursor:pointer'},['Confirm booking'])
      ]);
      form.addEventListener('submit',function(event){
        event.preventDefault();
        var formData=new FormData(form);
        var notes=formData.get('notes');
        setStatus('Confirming your booking...',false);
        fetch(bookingsApi,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({tenant_id:tenantId,service_id:service.id,slot_start:state.selectedSlot.start,slot_end:state.selectedSlot.end,customer:{firstName:String(formData.get('firstName')||''),lastName:String(formData.get('lastName')||''),email:String(formData.get('email')||''),phone:String(formData.get('phone')||'')||undefined},notes:typeof notes==='string'&&notes.length>0?notes:undefined})}).then(function(response){
          if(!response.ok){return response.json().catch(function(){return{error:'Booking failed'}}).then(function(body){throw new Error(body&&body.error?body.error:'Booking failed');});}
          return response.json();
        }).then(function(payload){
          form.reset();
          state.selectedSlot=null;
          renderSlots([]);
          setStatus('Booking confirmed. Reference '+(payload&&payload.data&&payload.data.bookingId?payload.data.bookingId:'generated')+'.',false);
          fetchAvailability();
        }).catch(function(error){
          var message=error instanceof Error?error.message:'Booking failed';
          setStatus(message+(bookingUrl&&bookingUrl!=='#'?' You can also use the fallback booking link below.':''),true);
        });
      });
      formWrap.appendChild(form);
    }

    function runIntentSearch(){
      if(state.mode!=='intent_first'){
        renderDiscovery();
        renderServices();
        return;
      }
      if(state.query.trim().length<2){
        state.intent={summary:'',source:'ai',matches:[],loading:false,error:''};
        renderDiscovery();
        renderServices();
        return;
      }
      state.intent.loading=true;
      state.intent.error='';
      renderDiscovery();
      renderServices();
      var params=new URLSearchParams({tenant_id:tenantId,query:state.query.trim()});
      fetch(intentApi+'?'+params.toString()).then(function(response){
        if(!response.ok)throw new Error('intent_request_failed');
        return response.json();
      }).then(function(payload){
        var data=payload&&payload.data?payload.data:{};
        state.intent={
          summary:data.summary||'',
          source:data.source==='fallback'?'fallback':'ai',
          matches:Array.isArray(data.matches)?data.matches:[],
          loading:false,
          error:''
        };
        if(state.intent.matches.length){
          var nextId=state.intent.matches[0].serviceId;
          if(nextId){state.selectedServiceId=nextId;}
        }
        renderDiscovery();
        renderServices();
      }).catch(function(){
        state.intent.loading=false;
        state.intent.error='Live intent matching is temporarily unavailable. You can still browse the public catalog below.';
        renderDiscovery();
        renderServices();
      });
    }

    function boot(){
      setStatus('Loading bookable services...',false);
      fetch(servicesApi+'?'+new URLSearchParams({tenant_id:tenantId}).toString()).then(function(response){
        if(!response.ok)throw new Error('service_catalog_failed');
        return response.json();
      }).then(function(payload){
        state.services=payload&&payload.data&&Array.isArray(payload.data.services)?payload.data.services:[];
        state.mode=payload&&payload.data&&payload.data.recommendedMode?payload.data.recommendedMode:'simple_list';
        if(!state.selectedServiceId&&state.services[0]){state.selectedServiceId=state.services[0].id;}
        renderModes();
        renderDiscovery();
        renderServices();
        if(!state.services.length){setStatus('No public services are ready for booking yet.',true);}
      }).catch(function(){
        setStatus('Booking configuration unavailable right now. Use the fallback link if you need a temporary path.',true);
      });
    }

    boot();
  })()</script>`;
}

export function renderServiceBookingBlock(
  props: Record<string, unknown>,
  ctx: RenderContext,
  defaultHeading: string,
): string {
  const p = props as ServiceBookingBlockProps;
  const heading = p.heading ?? defaultHeading;
  const serviceId = p.serviceId ?? p.calendarId ?? "";
  const duration = typeof p.duration === "number" ? p.duration : 30;
  const showTimezone = p.showTimezone !== false;
  const primaryColor = sanitizeThemeTokenColor(p.primaryColor ?? "")
    ?? "var(--ease-color-brand,#3b82f6)";
  const bookingUrl = p.bookingUrl ?? "#";
  const availabilityApiUrl = p.availabilityApiUrl ?? "/api/public/availability";
  const uid = `booking-${attr(String(props.id ?? serviceId ?? heading).replace(/[^a-z0-9]/gi, "").slice(0, 16))}`;
  const timezoneHtml = showTimezone
    ? `\n  <div data-booking-timezone class="ease-booking-timezone" style="font-size:0.875rem;color:var(--ease-color-text-soft,#6b7280);margin-top:8px"></div>`
    : "";

  return `<section class="ease-section ease-booking-widget" aria-label="${attr(heading)}" data-booking-id="${attr(uid)}" data-booking-surface="service-delivery" data-booking-measurement="live_availability" data-service-id="${attr(serviceId)}" data-tenant-id="${attr(ctx.tenantId)}" data-booking-url="${attr(bookingUrl)}" data-duration="${attr(String(duration))}">
  <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap;margin-bottom:20px">
    <div>
      <h2 class="ease-booking-heading" style="margin:0;color:var(--ease-color-text,#1f2937)">${text(heading)}</h2>
      <p style="margin:8px 0 0;color:var(--ease-color-text-soft,#6b7280);max-width:560px">This published Stero widget loads the tenant&apos;s live public service catalog, lets visitors pick a mode, checks real availability, and creates the booking inside the same runtime.</p>
    </div>
    <div style="border:1px solid var(--ease-color-border,var(--ease-color-muted,#e5e7eb));border-radius:18px;padding:12px 14px;background:var(--ease-color-surface,var(--ease-color-bg,#fff));color:var(--ease-color-text-soft,#6b7280);font-size:13px">
      Live public booking widget
    </div>
  </div>
  <div class="ease-booking-layout" style="display:grid;gap:20px;grid-template-columns:minmax(0,1.08fr) minmax(320px,0.92fr)">
    <div style="border:1px solid var(--ease-color-border,var(--ease-color-muted,#e5e7eb));border-radius:28px;padding:20px;background:linear-gradient(145deg,var(--ease-color-surface,var(--ease-color-bg,#fff)),color-mix(in srgb,var(--ease-color-surface,var(--ease-color-bg,#fff)) 82%,transparent))">
      <div data-booking-modes style="display:grid;gap:12px;grid-template-columns:repeat(3,minmax(0,1fr))"></div>
      <div data-booking-discovery style="margin-top:16px"></div>
      <div data-booking-services style="margin-top:16px;display:grid;gap:12px"></div>
    </div>
    <div style="border:1px solid var(--ease-color-border,var(--ease-color-muted,#e5e7eb));border-radius:28px;padding:20px;background:var(--ease-color-bg,#fff)">
      <h3 data-booking-selection-title style="margin:0;color:var(--ease-color-text,#1f2937)">Select a service</h3>
      <div data-booking-dates style="margin-top:16px;display:grid;gap:8px;grid-template-columns:repeat(2,minmax(0,1fr))"></div>
      <div data-booking-slots style="margin-top:16px;display:grid;gap:8px;grid-template-columns:repeat(2,minmax(0,1fr))"></div>
      <div data-booking-form style="margin-top:16px"></div>
      <div data-booking-status style="margin-top:16px"></div>${timezoneHtml}
    </div>
  </div>
  <noscript>
    <p><a href="${url(bookingUrl)}" style="color:${attr(primaryColor)}">Book your appointment</a></p>
  </noscript>
</section>
${buildScript(uid, serviceId, availabilityApiUrl)}`;
}
